import React, { useState, useEffect } from 'react';
import { ReactiveList, SelectedFilters, ReactiveBase } from '@appbaseio/reactivesearch';
import styled from '@emotion/styled';

import DataTableWrapper from '../DataTable/DataTableWrapper';
import DataTable from '../DataTable';
import Flex from '../styled/Flex';
import SortFilter from '../DataTable/SortFilter';
import NoResultFound from '../DataTable/NoResultFound';
import GlobalSearch from '../DataTable/GlobalSearch';

import { numberWithCommas } from '../../utils';

const RESULT_ROWS = 15;

const invertSort = currentSort => (currentSort === 'asc' ? 'desc' : 'asc');

const Container = styled.div`
	padding: 20px;
`;

const Table = () => {
	const [initialized, setInitialized] = useState(false);
	const [columns, setColumns] = useState([]);
	const [sortColumn, setSortColumn] = useState('_score');
	const [sort, setSort] = useState('desc');
	const [stats, setStats] = useState('stats');
	const [reactiveKey, setReactiveKey] = useState(Date.now());

	useEffect(() => {
		if (!initialized) {
			setReactiveKey(Date.now());
			setInitialized(true);
		}
	});

	function handleColumnChange(results = []) {
		if (Boolean(results.length) && columns.length <= 1) {
			const cols = Object.keys(results[0]).sort();
			setColumns(cols);
		}
	}

	function handleSort(sortField) {
		if (sortField === sortColumn) {
			setSort(invertSort(sort));
		} else {
			setSortColumn(sortField);
			setSort('asc');
		}
	}

	function resetSort() {
		setSort('desc');
		setSortColumn('_score');
	}

	const hideColumns = ['_id', 'highlight', '_type', '_index', '_score', '_click_id'];

	const cols = [...new Set([...columns])].filter(name => !hideColumns.includes(name));
	const visibleColumns = [...new Set(['_id', ...cols])];

	const sortingColumn = sortColumn;
	return (
		<ReactiveBase
			app="datagrid-movies-app"
			credentials="5e6b9f892f3d:b19b51ea-635f-48af-8b92-af9d842d1d84"
			url="https://appbase-demo-ansible-abxiydt-arc.searchbase.io"
			enableAppbase
		>
			<Container>
				<Flex>
					<GlobalSearch />
				</Flex>

				<Flex alignItems="center" justifyContent="space-between">
					<Flex style={{ minWidth: '60%' }}>
						<SortFilter field={sortingColumn} order={sort} onResetSort={resetSort} />
						<SelectedFilters />
					</Flex>
				</Flex>

				<DataTableWrapper
					visibleColumns={visibleColumns}
					handleSort={handleSort}
					sortColumn={sortingColumn}
					sortDir={sort}
				>
					{({ height, width, headerRef }) => (
						<div>
							<ReactiveList
								key={`Result_${reactiveKey}`}
								componentId={`Result_${reactiveKey}`}
								dataField={sortingColumn}
								sortBy={sort}
								size={RESULT_ROWS}
								react={{
									and: [...new Set(cols), 'GlobalSearch'],
								}}
								onData={({ data, resultStats }) => {
									handleColumnChange(data);
									setStats(resultStats);
								}}
								pagination={false}
								infiniteScroll={true}
								showResultStats={true}
								renderResultStats={statistics => (
									<Flex
										justifyContent="center"
										alignItems="center"
										style={{
											display: 'block',
											position: 'absolute',
											left: '0',
											height: '32px',
											fontSize: '14px',
											padding: '0 15px',
											lineHeight: '1.5',
											textAlign: 'center',
											bottom: -45,
										}}
									>
										Showing{' '}
										<b>{numberWithCommas(statistics.displayedResults || 0)}</b>{' '}
										of total{' '}
										<b>{numberWithCommas(statistics.numberOfResults || 0)}</b>
									</Flex>
								)}
								innerClass={{
									pagination: 'custom-pagination',
								}}
							>
								{({ error, data, loadMore }) => (
									<>
										{error && <NoResultFound />}
										{data.length ? (
											<>
												<DataTable
													key={data.length ? data[0]._id : '0'}
													data={data}
													columns={cols}
													height={height}
													width={width}
													headerRef={headerRef}
													stats={stats}
													loadMore={loadMore}
													pageSize={RESULT_ROWS}
												/>
											</>
										) : (
											<NoResultFound />
										)}
									</>
								)}
							</ReactiveList>
						</div>
					)}
				</DataTableWrapper>
			</Container>
		</ReactiveBase>
	);
};

export default Table;
